/** TodoDetailDrawer.tsx — 일일 업무 상세 (의뢰자=편집 / 담당자=읽기) + 댓글 스레드 */
import { useState, useEffect, useMemo } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { C, T, R } from './ui';
import * as todosApi from '../api-todos';
import type { Todo, TodoAttachment } from '../types';
import AssigneeChipPicker, { type Staff } from './AssigneeChipPicker';
import DueChipPicker from './DueChipPicker';
import TodoAttachments from './TodoAttachments';
import TodoCommentThread from './TodoCommentThread';
import TodoCheckBox from './TodoCheckBox';
import PhoneRequestPanel from './PhoneRequestPanel';

interface Props {
  todoId: string;
  initial?: Todo;
  staff: Staff[];
  myId: string;
  onClose: () => void;
  onUpdated: (t: Todo) => void;
  onDeleted: (id: string) => void;
  onToast?: (msg: string, kind?: 'success' | 'error') => void;
}

function fmtDate(d: string | null): string {
  if (!d) return '없음';
  return String(d).slice(0, 10);
}

export default function TodoDetailDrawer({ todoId, initial, staff, myId, onClose, onUpdated, onDeleted, onToast }: Props) {
  const [todo, setTodo] = useState<Todo | null>(null);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [assignees, setAssignees] = useState<string[]>([]);
  const [due, setDue] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<TodoAttachment[]>([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState<'' | 'restore' | 'remove'>('');   // 브라우저 확인 창 대신 단추를 한 번 더(2026-10-22)

  useEffect(() => {
    const apply = (found: Todo) => {
      setTodo(found);
      setTitle(found.title); setDesc(found.description || '');
      const a = Array.isArray(found.assignees) && found.assignees.length > 0
        ? found.assignees : [found.assigned_to].filter(Boolean);
      setAssignees(a);
      setDue(found.due_date ? String(found.due_date).slice(0, 10) : null);   // DB 가 ISO 전체(…T00:00:00.000Z)로 줄 수 있음 — 날짜 칸(type=date)은 YYYY-MM-DD 만 받는다(2026-10-09 마감일 칸이 비어 보이던 것)
      setAttachments(Array.isArray(found.attachments) ? found.attachments : []);
    };
    if (initial && initial.id === todoId) apply(initial);
    todosApi.getTodos({ status: 'all', limit: 100 }).then(r => {
      const found = r.data?.items?.find(t => t.id === todoId);
      if (found) apply(found);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todoId]);

  const iAmCreator = useMemo(() => todo?.created_by === myId, [todo, myId]);
  const creatorStaff = useMemo(() => staff.find(s => s.id === todo?.created_by), [staff, todo]);
  const assigneeStaffList = useMemo(
    () => assignees.map(id => staff.find(s => s.id === id)).filter(Boolean) as Staff[],
    [assignees, staff]
  );

  if (!todo) return null;

  function notifyCommentCount(n: number) {
    onUpdated({ ...todo!, comment_count: n, unread_comments: 0 });   // 댓글 창을 열면 읽음(서버도 GET 때 읽음으로) — 카드 깜빡임 멈춤
  }

  async function restoreToPending() {
    if (ask !== 'restore') { setAsk('restore'); return; }
    setAsk('');
    setBusy(true);
    try {
      const r = await todosApi.updateTodo(todoId, { status: 'pending', progress: 0, progress_note: null });
      if (r.success && r.data) {
        onUpdated(r.data); onToast?.('미완료로 되돌림', 'success'); onClose();
      } else onToast?.(r.error || '실패', 'error');
    } catch (e) { onToast?.(e instanceof Error ? e.message : '실패', 'error'); }
    finally { setBusy(false); }
  }

  async function saveCreator() {
    setBusy(true);
    try {
      const r = await todosApi.updateTodo(todoId, {
        title: title.trim() || todo!.title,
        description: desc.trim() || null,
        assignees,
        due_date: due,
      } as Partial<Todo>);
      if (r.success && r.data) {
        onUpdated(r.data); setDirty(false); onToast?.('저장됨', 'success');
      } else onToast?.(r.error || '저장 실패', 'error');
    } catch (e) { onToast?.(e instanceof Error ? e.message : '저장 실패', 'error'); }
    finally { setBusy(false); }
  }

  // 이미 맡긴 일도 「답 받고 내가 확인하기」를 켜고 끔(2026-10-22 배포 전 일은 모두 꺼져 있었음 · 답이 오기 전까지만)
  async function toggleCheck(on: boolean) {
    setBusy(true);
    try {
      const r = await todosApi.updateTodo(todoId, { needs_check: on } as Partial<Todo>);
      if (r.success && r.data) {
        setTodo(r.data); onUpdated(r.data);
        onToast?.(on ? '답을 받고 확인하는 일로 바꿨어요' : '확인 없이 끝내는 일로 바꿨어요', 'success');
      } else onToast?.(r.error || '바꾸지 못했어요', 'error');
    } catch (e) { onToast?.(e instanceof Error ? e.message : '바꾸지 못했어요', 'error'); }
    finally { setBusy(false); }
  }
  const canToggleCheck = !todo.is_template && !todo.result_at
    && (todo.status === 'pending' || todo.status === 'partial')
    && assignees.some(id => id !== myId);

  async function remove() {
    if (ask !== 'remove') { setAsk('remove'); return; }
    setAsk('');
    setBusy(true);
    try {
      await todosApi.deleteTodo(todoId);
      onDeleted(todoId); onClose();
    } catch (e) { onToast?.(e instanceof Error ? e.message : '삭제 실패', 'error'); }
    finally { setBusy(false); }
  }

  // ===================== 의뢰자 모달 =====================
  if (iAmCreator) {
    return (
      <Modal onClose={onClose}>
        <Header title="📌 일일 업무 상세 보기" onClose={onClose} />
        <Body>

        <Field label="제목">
          <input value={title} onChange={e => { setTitle(e.target.value); setDirty(true); }}
            style={{ width: '100%', padding: '8px 10px', border: `1px solid ${C.border}`,
              borderRadius: R.sm, fontSize: T.md, color: C.text }} />
        </Field>

        <Field label="메모 (요청사항 / 참고 내용)">
          <textarea value={desc} onChange={e => { setDesc(e.target.value); setDirty(true); }}
            onPaste={async e => {
              const items = e.clipboardData?.items;
              if (!items) return;
              const imgs: File[] = [];
              for (let i = 0; i < items.length; i++) {
                const it = items[i];
                if (it.kind === 'file' && it.type.startsWith('image/')) {
                  const f = it.getAsFile();
                  if (f) {
                    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
                    const ext = (f.type.split('/')[1] || 'png').split('+')[0];
                    imgs.push(new File([f], `paste-${ts}.${ext}`, { type: f.type }));
                  }
                }
              }
              if (imgs.length > 0) {
                e.preventDefault();
                for (const f of imgs) {
                  try {
                    const r = await todosApi.uploadAttachment(todoId, f);
                    if (r.success && r.data) setAttachments(prev => [...prev, r.data!]);
                  } catch { /* ignore */ }
                }
                onToast?.(`이미지 ${imgs.length}개 첨부 추가됨`, 'success');
              }
            }}
            placeholder="자세한 요청사항 / 참고 내용 (Ctrl+V 이미지 첨부)"
            style={{ width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`,
              borderRadius: R.sm, fontSize: T.sm, color: C.text, minHeight: 100, resize: 'vertical',
              lineHeight: 1.6 }} />
        </Field>

        <Field label="담당자 (복수 가능)">
          <AssigneeChipPicker value={assignees} staff={staff} myId={myId}
            onChange={ids => { setAssignees(ids); setDirty(true); }} />
        </Field>

        <Field label="마감일">
          <DueChipPicker value={due} onChange={v => { setDue(v); setDirty(true); }} />
        </Field>

        {todo.needs_check && (
          <Field label="답 확인">
            <TodoCheckBox todo={todo} staff={staff} myId={myId} onToast={onToast}
              onChange={t => { setTodo(t); onUpdated(t); }} />
          </Field>
        )}

        <Field label={`첨부파일 ${attachments.length > 0 ? `(${attachments.length})` : ''}`}>
          <TodoAttachments todoId={todoId} attachments={attachments}
            onChange={setAttachments} onToast={onToast} />
        </Field>

        <MoreOptions todoId={todoId} title={todo.title} onToast={onToast}
          check={canToggleCheck ? { on: !!todo.needs_check, busy, set: toggleCheck } : null} />

        <CommentArea>
          <TodoCommentThread todoId={todoId} myId={myId} creatorId={todo.created_by}
            onToast={onToast} onCountChange={notifyCommentCount} />
        </CommentArea>

        </Body>
        <Footer>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 8 }}>
          <button onClick={remove} disabled={busy}
            style={{ padding: '8px 14px', borderRadius: R.sm, border: `1px solid ${C.red}`,
              background: C.white, color: C.red, fontSize: T.sm, fontWeight: 600,
              cursor: busy ? 'wait' : 'pointer' }}>
            {ask === 'remove' ? '정말 삭제 (댓글 · 첨부 함께)' : '🗑 삭제'}
          </button>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {todo.status === 'done' && (
              <button onClick={restoreToPending} disabled={busy}
                style={{ padding: '8px 14px', borderRadius: R.sm,
                  border: `1px solid ${C.accent}`, background: C.white,
                  color: C.accent, fontSize: T.sm, fontWeight: 700,
                  cursor: busy ? 'wait' : 'pointer' }}>
                {ask === 'restore' ? '한 번 더 누르면 되돌려요' : '↺ 미완료로 되돌리기'}
              </button>
            )}
            <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: R.sm,
              border: `1px solid ${C.border}`, background: C.white,
              color: C.sub, fontSize: T.sm, cursor: 'pointer' }}>
              취소
            </button>
            <button onClick={saveCreator} disabled={!dirty || busy}
              style={{ padding: '8px 18px', borderRadius: R.sm, border: 'none',
                background: dirty ? C.accent : C.muted, color: C.white,
                fontSize: T.sm, fontWeight: 700, cursor: dirty && !busy ? 'pointer' : 'not-allowed' }}>
              {busy ? '저장중...' : '저장'}
            </button>
          </div>
        </div>
        </Footer>
      </Modal>
    );
  }

  // ===================== 담당자 모달 (읽기 + 댓글) =====================
  return (
    <Modal onClose={onClose}>
      <Header title={`📌 일일 업무 상세 보기 (의뢰: ${creatorStaff?.name || '?'})`} onClose={onClose} />
      <Body>

      <Field label="제목">
        <div style={{ padding: '8px 10px', background: C.bgAlt, borderRadius: R.sm,
          fontSize: T.md, fontWeight: 600, color: C.text }}>
          {todo.title}
        </div>
      </Field>

      <Field label="📝 요청사항 / 지시사항">
        <div style={{ padding: '12px 14px', background: C.bgAlt, borderRadius: R.sm,
          fontSize: T.sm, color: C.text, minHeight: 120, lineHeight: 1.7,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {desc || <span style={{ color: C.muted, fontStyle: 'italic' }}>요청사항 없음</span>}
        </div>
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <Field label="담당자">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {assigneeStaffList.length === 0 ? (
              <span style={{ fontSize: T.sm, color: C.muted }}>없음</span>
            ) : assigneeStaffList.map(a => (
              <span key={a.id} style={{
                padding: '4px 9px', borderRadius: R.sm,
                background: a.id === myId ? C.accentLt : C.bgAlt,
                color: a.id === myId ? C.accent : C.sub,
                fontSize: T.xs, fontWeight: 600,
              }}>
                {a.name}{a.id === myId ? ' (나)' : ''}
              </span>
            ))}
          </div>
        </Field>

        <Field label="마감일">
          <div style={{ padding: '6px 10px', background: C.bgAlt, borderRadius: R.sm,
            fontSize: T.sm, color: due ? C.text : C.muted }}>
            {fmtDate(due)}
          </div>
        </Field>
      </div>

      {todo.needs_check && (
          <Field label="답 확인">
            <TodoCheckBox todo={todo} staff={staff} myId={myId} onToast={onToast}
              onChange={t => { setTodo(t); onUpdated(t); }} />
          </Field>
        )}

        <Field label={`첨부파일 ${attachments.length > 0 ? `(${attachments.length})` : ''}`}>
        <TodoAttachments todoId={todoId} attachments={attachments}
          onChange={setAttachments} onToast={onToast} />
      </Field>

      <MoreOptions todoId={todoId} title={todo.title} onToast={onToast} check={null} />

      <CommentArea>
        <TodoCommentThread todoId={todoId} myId={myId} creatorId={todo.created_by}
          onToast={onToast} onCountChange={notifyCommentCount} />
      </CommentArea>

      </Body>
      <Footer>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: R.sm,
          border: `1px solid ${C.border}`, background: C.white,
          color: C.sub, fontSize: T.sm, cursor: 'pointer' }}>
          닫기
        </button>
      </div>
      </Footer>
    </Modal>
  );
}

function Modal({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.4)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.white,
        borderRadius: R.lg, width: '100%', maxWidth: 600,
        maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );
}

function Header({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
      <div style={{ fontSize: T.lg, fontWeight: 700, color: C.text }}>{title}</div>
      <button onClick={onClose} style={{ border: 'none', background: 'transparent',
        fontSize: T.lg, cursor: 'pointer', color: C.muted }}>✕</button>
    </div>
  );
}

function Body({ children }: { children: ReactNode }) {
  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '14px 20px' }}>
      {children}
    </div>
  );
}

function Footer({ children }: { children: ReactNode }) {
  return (
    <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: T.xs, color: C.sub, fontWeight: 600, marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

/** 더 하기 — 「답 받고 내가 확인하기」 · 「팀장님 요청으로 등록」을 같은 모양 줄 두 개로 한 상자에 (2026-10-27 지도사 「이 부분도 뭔가 조잡하다」)
 *  예전: 맨 체크박스 + 흐린 글 · 「팀장님 요청」 제목 + 큰 파란 단추 + 아래 설명 줄 — 모양 · 크기 · 위치가 제각각이었다
 *  줄 = 왼쪽 굵은 이름 + 아래 한 줄 설명 · 오른쪽 조작(켜기 스위치 · 「등록」) — 팀장님 요청을 열면 그 줄 아래에 입력판 */
function MoreOptions({ todoId, title, onToast, check }: {
  todoId: string; title: string; onToast?: (msg: string, kind?: 'success' | 'error') => void;
  check: { on: boolean; busy: boolean; set: (v: boolean) => void } | null;
}) {
  const [req, setReq] = useState(false);
  const row: CSSProperties = { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', alignItems: 'center', gap: 12, padding: '12px 14px' };
  const name: CSSProperties = { fontSize: T.sm, fontWeight: 700, color: C.text };
  const sub: CSSProperties = { fontSize: T.xs, color: C.sub, marginTop: 2, wordBreak: 'keep-all' };
  return (
    <div style={{ marginBottom: 12, border: `1px solid ${C.border}`, borderRadius: R.md, background: C.white }}>
      {check && (
        <label style={{ ...row, cursor: check.busy ? 'wait' : 'pointer' }}>
          <span><span style={{ display: 'block', ...name }}>답 받고 내가 확인하기</span><span style={{ display: 'block', ...sub }}>답이 오면 내가 확인하고 안내까지 해야 끝나요</span></span>
          <span role="switch" aria-checked={check.on} style={{ position: 'relative', width: 40, height: 24, borderRadius: 12, background: check.on ? C.accent : '#D1D5DB', transition: 'background .15s', flexShrink: 0 }}>
            <input type="checkbox" checked={check.on} disabled={check.busy} onChange={e => check.set(e.target.checked)} aria-label="답 받고 내가 확인하기"
              style={{ position: 'absolute', inset: 0, opacity: 0, margin: 0, cursor: 'inherit' }} />
            <span aria-hidden style={{ position: 'absolute', top: 3, left: check.on ? 19 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.25)', transition: 'left .15s' }} />
          </span>
        </label>
      )}
      <div style={{ ...row, borderTop: check ? `1px solid ${C.border}` : 'none' }}>
        <span><span style={{ display: 'block', ...name }}>팀장님 요청으로 등록</span><span style={{ display: 'block', ...sub }}>전화로 받은 요청이면 — 그 팀장님 고객관리 「맡긴 일」에 바로 보여요</span></span>
        <button type="button" onClick={() => setReq(v => !v)} aria-expanded={req}
          style={{ height: 32, minWidth: 64, padding: '0 12px', borderRadius: R.sm, border: `1px solid ${req ? C.border : C.accent}`, background: C.white, color: req ? C.sub : C.accent, fontSize: T.sm, fontWeight: 700, cursor: 'pointer' }}>
          {req ? '닫기' : '등록'}
        </button>
      </div>
      {req && <div style={{ padding: '0 14px 14px' }}><PhoneRequestPanel todoId={todoId} title={title} onClose={() => setReq(false)} onDone={(m) => { if (onToast) onToast(m, 'success'); }} /></div>}
    </div>
  );
}

/** 댓글 — 회색 상자 대신 위 구분선 + 제목(상자 안 상자가 겹쳐 보이던 것) */
function CommentArea({ children }: { children: ReactNode }) {
  return <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>{children}</div>;
}

